import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllPermitStatuses,
  useGetAllStatuses,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { useLocation } from "react-router-dom";
import { Button } from "../button";
import { useGetAllSearchApprovePermitRequests } from "../../hooks/permit/query";
import { SYSTEM_DETAIL_CODES } from "../../constants/common";

interface SearchPermitRequestCardProps {
  setAllPermits: Dispatch<SetStateAction<never[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
}

const SearchPermitRequestCardSchema = Yup.object({
  permitType: Yup.string(),
  permitStartDate: Yup.string(),
  permitEndDate: Yup.string(),
  status: Yup.string(),
});

const initialState = {
  permitStartDate: "",
  permitEndDate: "",
  status: "",
  permitType: "",
};

const SearchPermitRequestCard = ({
  setAllPermits,
  setIsLoading,
  page,
  setPages,
  search,
  setCount,
  pageSize,
}: SearchPermitRequestCardProps) => {
  const {
    control,
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchPermitRequestCardSchema);

  const status = watch("status");

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const params = useLocation();

  const { data: allStatuses } = useGetAllPermitStatuses();

  const fromDate = watch("permitStartDate");
  const toDate = watch("permitEndDate");

  const { data: allPermitTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.PERMIT_TYPES,
  });

  const permitTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allPermitTypes),
    [allPermitTypes]
  );

  const permitType = watch("permitType");

  const { data: permitRequests, isPending: isPermitRequestsLoading } =
    useGetAllSearchApprovePermitRequests({
      pageSize: pageSize,
      pageNumber: page,
      search: search,
      n_PermitTypeID: Number(permitType) || 0,
      n_ApprovalStatus: Number(status) || 0,
      permitStartDate: fromDate || "",
      permitEndDate: toDate || "",
    }, params.pathname);

  useEffect(() => {
    setIsLoading?.(isPermitRequestsLoading);
  }, [isPermitRequestsLoading, setIsLoading]);

  useEffect(() => {
    if (permitRequests) {
      setAllPermits(permitRequests.data || []);
      setPages(permitRequests.pagination?.pages || 1);
      setCount(permitRequests.pagination?.count || 0);
    }
  }, [params.pathname, setCount, setAllPermits, setPages, permitRequests]);

  useEffect(() => {
    if (search && status === "2") {
      setValue("status", "0");
    }
  }, [search, setValue, status]);

  const statuses = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allStatuses),
    [allStatuses]
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="permitType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={permitTypes}
                    onChange={onChange}
                    label={t("permitType")}
                    value={
                      permitTypes?.find((item) => item.value === value)?.labelEn ||
                      ""
                    }
                  />
                )}
              />

              <Controller
                name="permitStartDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("startDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                      status === "2" && setValue("status", "0");
                    }}
                  />
                )}
              />
              <Controller
                name="permitEndDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("endDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                      status === "2" && setValue("status", "0");
                    }}
                  />
                )}
              />
              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={statuses}
                    onChange={onChange}
                    label={t("status")}
                    value={
                      statuses?.find((item) => item.value === value)?.labelEn ||
                      ""
                    }
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default memo(SearchPermitRequestCard);
